<template>
	<el-row>
		<el-col :span="24">
			<div class="base-form">
				<h2 class="item" v-if="isCreatingViewMode">
					Новая категория сервисных работ
				</h2>
				<h2 class="item" v-else>
					Карточка категории <span>{{ serviceCategory?.name }}</span>
				</h2>
			</div>
		</el-col>		
	</el-row>
	<el-row>
		<el-col :span="24">
			<div class="base-form">
				<el-form :model="ruleForm" ref="jobForm" status-icon :rules="rules">
					<el-form-item label="Категория" label-position="top" prop="name">
						<el-input v-model="ruleForm.name" />
					</el-form-item>

					<el-form-item label="Стоимость нормо-часа (руб.)" label-position="top" prop="costPerHour">
						<el-input-number v-model="ruleForm.costPerHour" :min="0" :step="10" />
					</el-form-item>		

					<el-form-item label="Описание" label-position="top" prop="description">
						<el-input v-model="ruleForm.description"
							maxlength="255"
							show-word-limit
							type="textarea" />
					</el-form-item>		

					<el-form-item>
						<el-row style="width: 100%">
							<el-col :span="10">
								<el-button type="primary" :disabled="asyncOperationPending" @click="onFormSubmitClick">
									{{ isCreatingViewMode ? 'Создать' : 'Сохранить' }}
								</el-button>
								&nbsp;
								<el-button @click="onFormCancelClick">
									Отменить
								</el-button>
							</el-col>
							<el-col :span="14">
								<div v-if="!isCreatingViewMode" style="text-align: right;">
									<el-button type="danger" :disabled="!isDeletionAllowed || asyncOperationPending" @click="onFormDeleteClick">
										Удалить категорию
									</el-button>
								</div>
							</el-col>
						</el-row>
					</el-form-item>

					<el-row style="width: 100%">
						<el-col :span="10">
							&nbsp;
						</el-col>
						<el-col :span="14">
							<div v-if="!isCreatingViewMode && !isDeletionAllowed" style="text-align: right; line-height: 1rem; margin-top: 1rem">
								<el-text type="info" size="small">
									<el-icon><Opportunity /></el-icon>
									Категорию нельзя удалить - на нее ссылаются сервисные работы (ссылок {{referencedJobsCount}})
								</el-text>
							</div>
						</el-col>
					</el-row>
				</el-form>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import CatalogPickerComponent from '@/components/CatalogPickerComponent.vue';
import { ElButton, ElTable, ElLink, FormInstance, ElNotification, ElMessageBox } from 'element-plus';
import { reactive } from 'vue';
import { Opportunity } from '@element-plus/icons-vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import EventHelper from '@/helpers/eventsHelper';
import ServiceJobCategoryModel from '@/models/ServiceJobCategoryModel';

interface IServiceJobCategoryFormFields {
	id: number;
	name: string;
	costPerHour: number;
	description: string | null;
}

@Options({
	components: {
		ElButton, ElTable, ElLink, CatalogPickerComponent, Opportunity
	},
	props: {
		params: Array
	}
})
export default class ServiceJobCategoryView extends Vue {
	/** params */
	params!: Array<any>;
	isCreatingViewMode = false;

	/** constants */
	serviceCategory: ServiceJobCategoryModel | null = null;
	
	/** view-models */
	isDeletionAllowed = false;
	referencedJobsCount = 0;
	asyncOperationPending = false;
	ruleForm = reactive<IServiceJobCategoryFormFields>({
		id: 0,
		name: '',
		costPerHour: 0,
		description: null
	});

	rules = reactive({
		name: [
			{ required: true, message: 'Название категории не может быть пустым'},
			{ min: 1, max: 255, message: 'Название категории не может быть короче 1 и длинее 255 символов'},			
		],
		description: [
			{ max: 255, message: 'Описание категории не может быть длинее 255 символов'},			
		]
	});
	
	mounted() {
		this.initForm();
	}

	/**
	 * init form with service-job-category's data from incoming params
	 */
	async initForm() {
		if (!this.params?.length) {
			alert('Wrong view arguments')
			return;
		}

		if (this.params[1] === true) {
			this.isCreatingViewMode = true;
			return;
		}

		if (!!this.params[0]?.id &&
			!!this.params[0]?.name) {
				this.serviceCategory = new ServiceJobCategoryModel(this.params[0]);
				this.ruleForm.id = this.serviceCategory.id;
				this.ruleForm.name = this.serviceCategory.name;
				this.ruleForm.costPerHour = this.serviceCategory.costPerHour;
				this.ruleForm.description = this.serviceCategory.description;
				this.isCreatingViewMode = false;

				const relatedJobIdsToGroup = await this.getRelatedJobIdsAsync(this.serviceCategory.id);
				this.referencedJobsCount = relatedJobIdsToGroup?.length ?? 0;
				this.isDeletionAllowed = relatedJobIdsToGroup?.length === 0;
				return;
		}

		alert('Wrong view arguments');
	}

	async onFormSubmitClick() {
		const form = this.$refs.jobForm as FormInstance;
		form.validate(async (valid) => {
			if (valid) {
				try {
					this.asyncOperationPending = true;
					if (this.isCreatingViewMode) {
						await this.addCategory();
					} else {
						await this.updateCategoryAsync();
					}
					ElNotification({
						message: `Сервисная категория успешно ${this.isCreatingViewMode ? 'создана' : 'обновлена'}`,
						type: 'success',
					});
					EventHelper.dispatchEvent('jobOperationsUpdate');
					this.closeCurrentTab();
				} catch (e: any) {
					if (e.code == 'ERR_NETWORK') {
						ElNotification({
							message: `Ошибка соединения, не удалось ${this.isCreatingViewMode ? 'создать' : 'обновить'} сервисную категорию`,
							type: 'error',
						});
					} else {
						ElNotification({
							message: `Ошибка ${this.isCreatingViewMode ? 'создания новой' : 'обновления'} сервисной категории`,
							type: 'error',
						});
					}
					console.error('Error submitting service-job-category', e);
				}
				finally {
					this.asyncOperationPending = false;
				}
			}			
		});
	}

	onFormCancelClick() {
		this.closeCurrentTab();
	}

	/** deletes an existing service-job-category */
	async onFormDeleteClick(e: Event) {
		e.stopPropagation();

		const relatedJobIdsToCategory = await this.getRelatedJobIdsAsync(this.ruleForm.id);

		if (relatedJobIdsToCategory === null) {
			ElNotification({
				message: 'Ошибка удаления категории сервисных работ',
				type: 'error',
			});
			return;
		}

		if (relatedJobIdsToCategory.length == 0) {
			ElMessageBox.confirm(
				`Категория сервисных работ '${this.ruleForm.name}' будет удалена`,
				{
					confirmButtonText: 'OK',
					cancelButtonText: 'Отмена',
					type: 'error',
				}
			)
			.then(() => this.deleteCategory(this.ruleForm.id));
		} else {
			ElMessageBox.alert(
				`Невозможно удалить категорию '${this.serviceCategory!.name}', на нее ссылаются сервисные работы (${relatedJobIdsToCategory.length})`,
				'Удаление'
			);
		}
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}

	async addCategory() {
		const request = {
			name: this.ruleForm.name,
			costPerHour: this.ruleForm.costPerHour,
			description: this.ruleForm.description
		};
		const jwt = store.getters.jwt;
		const a = axiosHelper.createInstance(jwt);
		await a.post('/api/servicejobcategories', request);
	}

	updateCategoryAsync() : Promise<void> {
		const request = {
			id: this.ruleForm.id,
			name: this.ruleForm.name,
			costPerHour: this.ruleForm.costPerHour,
			description: this.ruleForm.description
		};
		const jwt = store.getters.jwt;
		const a = axiosHelper.createInstance(jwt);
		return a.put('/api/servicejobcategories', request);
	}

	async deleteCategory(categoryId: number) {
		try {
			this.asyncOperationPending = true;
			const jwt = store.getters.jwt;
			const a = axiosHelper.createInstance(jwt);
			await a.delete(`/api/servicejobcategories/${categoryId}`);
			ElNotification({
				title: 'Сервисная категория удалена',
				type: 'success',
			});
			EventHelper.dispatchEvent('jobCategoriesUpdate');
			this.closeCurrentTab();	
			this.asyncOperationPending = false;
		} catch (e) {
			ElNotification({
				title: 'Ошибка, сервисная категория не была удалена',
				type: 'error',
			});
			console.error(`Error deleting service-job-category #${categoryId}`, e)
		} finally {
			this.asyncOperationPending = false;
		}
	}

	async getRelatedJobIdsAsync(categoryId: number): Promise<Array<number> | null> {
		try {
			const jwt = store.getters.jwt;
			const jobCategoriesHttpClient = axiosHelper.createInstance(jwt);
			const jobCategoriesResponse = await jobCategoriesHttpClient.get(`/api/servicejobcategories/rels/${categoryId}`);
			return jobCategoriesResponse.data.map((_: number) => _);
		} catch (e) {			
			console.error(`Error checking service-job-category #${categoryId} for related jobs`, e);
			return null;
		}
	}
}
</script>


<style scoped>
h2.item span {
	color: var(--el-color-primary);
}
</style>